<template>
  <div class="knowledge-base-container">
    <!-- Top Header -->
    <div class="page-header">
      <h1>Knowledge Base</h1>
      <div class="header-actions">
        <button 
          v-if="!showCreateKBForm" 
          @click="showCreateKBForm = true" 
          class="btn btn-primary"
        >
          <i class="icon-plus"></i> New Knowledge Base
        </button>
      </div>
    </div>
    
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-if="success" class="success-message">
      {{ success }}
    </div>
    
    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Left Panel: Knowledge Bases List -->
      <div class="left-panel">
        <div class="panel-header">
          <h3>Knowledge Bases</h3>
          <span class="kb-count">{{ knowledgeBases.length }} bases</span>
        </div>
        
        <div v-if="knowledgeBases.length === 0" class="no-data">
          <div class="empty-state">
            <i class="icon-knowledge"></i>
            <h4>No knowledge bases yet</h4>
            <p>Create your first knowledge base to organize documents</p>
          </div>
        </div>
        
        <div v-else class="kb-list">
          <div 
            v-for="kb in knowledgeBases" 
            :key="kb.id" 
            class="kb-item"
            :class="{ 'active': selectedKB?.id === kb.id }"
            @click="selectKB(kb)"
          >
            <div class="kb-avatar">
              <i class="icon-folder"></i>
            </div>
            <div class="kb-info">
              <h4>{{ kb.name }}</h4>
              <p>{{ kb.description }}</p>
              <div class="kb-meta">
                <span class="doc-count">{{ kb.document_count || 0 }} docs</span>
                <span v-if="kb.is_active === false" class="kb-status inactive">Inactive</span>
              </div>
            </div>
            <div class="kb-actions">
              <button @click.stop="editKB(kb)" class="btn-icon" title="Edit">
                <i class="icon-edit"></i>
              </button>
              <button @click.stop="deleteKB(kb)" class="btn-icon danger" title="Archive">
                <i class="icon-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Right Panel: Documents/Forms -->
      <div class="right-panel">
        <!-- Create Knowledge Base Form -->
        <div v-if="showCreateKBForm || editingKB" class="kb-form-panel">
          <div class="panel-header">
            <h3>{{ editingKB ? 'Edit Knowledge Base' : 'Create Knowledge Base' }}</h3>
            <button @click="closeKBForm" class="btn-close">
              <i class="icon-close"></i>
            </button>
          </div>
          
          <form @submit.prevent="editingKB ? updateKB() : createKB()" class="kb-form">
            <div class="form-group">
              <label>Name *</label>
              <input
                v-model="kbForm.name"
                type="text"
                placeholder="Enter knowledge base name"
                required
              >
            </div>
            
            <div class="form-group">
              <label>Description *</label>
              <textarea
                v-model="kbForm.description"
                placeholder="Describe this knowledge base purpose"
                required
                rows="3"
              ></textarea>
            </div>
            
            <!--
            <div class="form-group">
              <label>Embedding Model</label>
              <select v-model="kbForm.embedding_model">
                <option value="openai/text-embedding-ada-002">OpenAI Text Embedding Ada 002</option>
                <option value="openai/text-embedding-3-small">OpenAI Text Embedding 3 Small</option>
                <option value="openai/text-embedding-3-large">OpenAI Text Embedding 3 Large</option>
                <option value="cohere/embed-english-v3.0">Cohere Embed English v3.0</option>
              </select>
            </div>
            -->
            
            <!--
            <div class="form-group">
              <label>Tags (comma-separated)</label>
              <input
                v-model="kbForm.tags"
                type="text"
                placeholder="research, documentation, support"
              >
            </div>
            -->
            
            <div class="form-actions">
              <button type="button" @click="closeKBForm" class="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                {{ editingKB ? 'Update Knowledge Base' : 'Create Knowledge Base' }}
              </button>
            </div>
          </form>
        </div>
        
        <!-- Upload Document Form -->
        <div v-else-if="showUploadForm" class="upload-form-panel">
          <div class="panel-header">
            <h3>Upload Document</h3>
            <button @click="closeUploadForm" class="btn-close">
              <i class="icon-close"></i>
            </button>
          </div>
          
          <form @submit.prevent="uploadDocument" class="upload-form">
            <div class="form-group">
              <label>Knowledge Base *</label>
              <select v-model="uploadForm.knowledge_base_id" required>
                <option value="">Select a knowledge base</option>
                <option v-for="kb in knowledgeBases" :key="kb.id" :value="kb.id">
                  {{ kb.name }}
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label>Document File *</label>
              <div class="file-upload-zone" 
                @drop.prevent="handleFileDrop" 
                @dragover.prevent 
                @dragenter.prevent
                :class="{ 'drag-over': isDragOver }"
                @dragenter="isDragOver = true"
                @dragleave="isDragOver = false"
                @click="$refs.fileInput.click()"
              >
                <input 
                  type="file" 
                  ref="fileInput"
                  @change="handleFileSelect"
                  accept=".pdf,.txt,.docx,.md,.doc,.xlsx,.xls"
                  hidden
                >
                <div v-if="!selectedFile" class="upload-placeholder">
                  <i class="icon-upload-big"></i>
                  <h4>Drop files here or click to browse</h4>
                  <p>Supported formats: PDF, TXT, DOCX, MD, DOC, XLSX, XLS</p>
                  <button type="button" @click="$refs.fileInput.click()" class="btn btn-outline">
                    Choose Files
                  </button>
                </div>
                <div v-else class="file-preview">
                  <div class="file-info">
                    <i class="icon-file"></i>
                    <div class="file-details">
                      <h4>{{ selectedFile.name }}</h4>
                      <p>{{ formatFileSize(selectedFile.size) }}</p>
                    </div>
                    <button type="button" @click="removeFile" class="btn-remove">
                      <i class="icon-close"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="form-group">
              <label>Document Title</label>
              <input
                v-model="uploadForm.title"
                type="text"
                placeholder="Custom title (optional, will use filename if empty)"
              >
            </div>
            
            <div class="form-group">
              <label>Tags (comma-separated)</label>
              <input
                v-model="uploadForm.tags"
                type="text"
                placeholder="important, guide, tutorial"
              >
            </div>
            
            <div class="form-group">
              <label>Category</label>
              <select v-model="uploadForm.category">
                <option value="">Select category</option>
                <option value="documentation">Documentation</option>
                <option value="research">Research</option>
                <option value="policy">Policy</option>
                <option value="tutorial">Tutorial</option>
                <option value="reference">Reference</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>Description</label>
              <textarea
                v-model="uploadForm.description"
                placeholder="Brief description of the document content"
                rows="3"
              ></textarea>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeUploadForm" class="btn btn-secondary">
                Cancel
              </button>
              <button 
                type="submit" 
                class="btn btn-primary"
                :disabled="!selectedFile || !uploadForm.knowledge_base_id || isUploading"
              >
                {{ isUploading ? 'Uploading...' : 'Upload Document' }}
              </button>
            </div>
          </form>
        </div>
        
        <!-- Documents List -->
        <div v-else-if="selectedKB" class="documents-panel">
          <div class="panel-header">
            <div class="kb-title">
              <h3>{{ selectedKB.name }}</h3>
              <span class="doc-count-badge">{{ documents.length }} documents</span>
            </div>
            <div class="header-actions">
              <button @click="showUploadForm = true" class="btn btn-primary">
                <i class="icon-upload"></i> Upload
              </button>
              <button @click="editKB(selectedKB)" class="btn btn-outline">
                <i class="icon-edit"></i> Edit
              </button>
            </div>
          </div>
          
          <!-- Search and Filters -->
          <div class="documents-toolbar">
            <div class="search-box">
              <i class="icon-search"></i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search documents..."
                @input="filterDocuments"
              >
            </div>
            <div class="filter-controls">
              <select v-model="selectedCategory" @change="filterDocuments">
                <option value="">All Categories</option>
                <option value="documentation">Documentation</option>
                <option value="research">Research</option>
                <option value="policy">Policy</option>
                <option value="tutorial">Tutorial</option>
                <option value="reference">Reference</option>
                <option value="other">Other</option>
              </select>
              <select v-model="sortBy" @change="sortDocuments">
                <option value="created_at">Date Created</option>
                <option value="name">Name</option>
                <option value="size">Size</option>
                <option value="category">Category</option>
              </select>
            </div>
          </div>
          
          <!-- KB Overview -->
          <div class="kb-overview">
            <p class="kb-description">{{ selectedKB.description }}</p>
            <div class="kb-stats">
              <div class="stat-item">
                <span class="label">Documents:</span>
                <span class="value">{{ documents.length }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Total Size:</span>
                <span class="value">{{ formatTotalSize() }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Last Updated:</span>
                <span class="value">{{ formatDate(selectedKB.updated_at) }}</span>
              </div>
            </div>
          </div>
          
          <!-- Documents Grid -->
          <div class="documents-section">
            <div v-if="filteredDocuments.length === 0" class="no-documents">
              <i class="icon-document"></i>
              <h4>No documents found</h4>
              <p v-if="searchQuery || selectedCategory">Try adjusting your search or filters</p>
              <p v-else>Upload your first document to get started</p>
              <button v-if="!searchQuery && !selectedCategory" @click="showUploadForm = true" class="btn btn-primary">
                <i class="icon-upload"></i> Upload Document
              </button>
            </div>
            
            <div v-else class="documents-grid">
              <div 
                v-for="doc in filteredDocuments" 
                :key="doc.id" 
                class="document-card"
                @click="viewDocument(doc)"
              >
                <div class="doc-header">
                  <div class="doc-icon">
                    <i :class="getFileIcon(doc.content_type)"></i>
                  </div>
                  <div class="doc-actions">
                    <button @click.stop="downloadDocument(doc)" class="btn-icon" title="Download">
                      <i class="icon-download"></i>
                    </button>
                    <button @click.stop="deleteDocument(doc)" class="btn-icon danger" title="Delete">
                      <i class="icon-trash"></i>
                    </button>
                  </div>
                </div>
                
                <div class="doc-content">
                  <h4>{{ doc.title || doc.name }}</h4>
                  <p class="doc-description">{{ doc.description || 'No description' }}</p>
                  
                  <div class="doc-meta">
                    <div class="meta-row">
                      <span class="meta-label">Size:</span>
                      <span class="meta-value">{{ formatFileSize(doc.metadata?.size) }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">Type:</span>
                      <span class="meta-value">{{ getFileType(doc.content_type) }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">Category:</span>
                      <span class="meta-value category" :class="doc.category || 'uncategorized'">
                        {{ doc.category || 'Uncategorized' }}
                      </span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">Created:</span>
                      <span class="meta-value">{{ formatDate(doc.created_at) }}</span>
                    </div>
                  </div>
                  
                  <div v-if="doc.tags && doc.tags.length > 0" class="doc-tags">
                    <span v-for="tag in doc.tags" :key="tag" class="tag">
                      {{ tag }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Welcome Panel -->
        <div v-else class="welcome-panel">
          <div class="welcome-content">
            <i class="icon-knowledge-large"></i>
            <h3>Welcome to Knowledge Base</h3>
            <p>Organize and manage your documents efficiently. Create knowledge bases to categorize content, upload files with tags and metadata, and search through your organization's knowledge.</p>
            <div class="welcome-actions">
              <button @click="showCreateKBForm = true" class="btn btn-primary">
                <i class="icon-plus"></i> Create Knowledge Base
              </button>
              <button @click="showUploadForm = true" class="btn btn-outline">
                <i class="icon-upload"></i> Upload Document
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'KnowledgeBaseView',
  setup() {
    const store = useStore()
    const knowledgeBases = computed(() => {
      const rawKBs = store.state.knowledgeBases?.items || []
      return Array.isArray(rawKBs) ? rawKBs.filter(kb => kb && typeof kb === 'object' && kb.id) : []
    })
    const documents = computed(() => store.state.knowledgeBases?.documents || [])
    
    const selectedKB = ref(null)
    const editingKB = ref(null)
    const selectedFile = ref(null)
    const error = ref('')
    const success = ref('')
    const showCreateKBForm = ref(false)
    const showUploadForm = ref(false)
    const isDragOver = ref(false)
    const isUploading = ref(false)
    const searchQuery = ref('')
    const selectedCategory = ref('')
    const sortBy = ref('created_at')
    const filteredDocuments = ref([])
    
    // Forms
    const kbForm = reactive({
      name: '',
      description: '',
      embedding_model: 'openai/text-embedding-ada-002',
      tags: ''
    })
    
    const uploadForm = reactive({
      knowledge_base_id: '',
      title: '',
      tags: '',
      category: '',
      description: ''
    })

    const fetchKnowledgeBases = async () => {
      try {
        await store.dispatch('knowledgeBases/fetchAll')
      } catch (err) {
        console.error('Error fetching knowledge bases:', err)
        // Add sample data for demo purposes
        store.commit('SET_KNOWLEDGE_BASES', [
          {
            id: 'kb-1',
            name: 'Product Documentation',
            description: 'Technical documentation and user guides for our products',
            document_count: 15,
            is_active: true,
            embedding_model: 'openai/text-embedding-ada-002',
            tags: ['documentation', 'technical'],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 'kb-2',
            name: 'Customer Support',
            description: 'FAQ and support articles for customer service',
            document_count: 8,
            is_active: true,
            embedding_model: 'openai/text-embedding-ada-002',
            tags: ['support', 'faq'],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ])
      }
    }

    const fetchDocuments = async (kbId) => {
      try {
        await store.dispatch('knowledgeBases/getDocuments', kbId)
        filterDocuments()
      } catch (err) {
        console.error('Error fetching documents:', err)
        error.value = 'Failed to fetch documents'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const selectKB = (kb) => {
      selectedKB.value = kb
      editingKB.value = null
      showCreateKBForm.value = false
      showUploadForm.value = false
      uploadForm.knowledge_base_id = kb.id
      fetchDocuments(kb.id)
    }
    
    const editKB = (kb) => {
      editingKB.value = kb
      showCreateKBForm.value = false
      showUploadForm.value = false
      
      kbForm.name = kb.name
      kbForm.description = kb.description
      kbForm.embedding_model = kb.embedding_model || 'openai/text-embedding-ada-002'
      kbForm.tags = kb.tags ? kb.tags.join(', ') : ''
    }
    
    const resetKBForm = () => {
      kbForm.name = ''
      kbForm.description = ''
      kbForm.embedding_model = 'openai/text-embedding-ada-002'
      kbForm.tags = ''
    }
    
    const closeKBForm = () => {
      showCreateKBForm.value = false
      editingKB.value = null
      resetKBForm()
    }

    const createKB = async () => {
      try {
        const result = await store.dispatch('knowledgeBases/create', {
          name: kbForm.name,
          description: kbForm.description
        })
        
        success.value = 'Knowledge base created successfully!'
        setTimeout(() => success.value = '', 3000)
        
        await fetchKnowledgeBases()
        resetKBForm()
        showCreateKBForm.value = false
        
        // Select the newly created KB
        const newKB = knowledgeBases.value.find(kb => kb.id === result.id)
        if (newKB) {
          selectKB(newKB)
        }
      } catch (err) {
        console.error('Error creating knowledge base:', err)
        error.value = 'Failed to create knowledge base'
        setTimeout(() => error.value = '', 3000)
      }
    }
    
    const updateKB = async () => {
      if (!editingKB.value) return
      
      try {
        await store.dispatch('knowledgeBases/update', {
          id: editingKB.value.id,
          updates: {
            name: kbForm.name,
            description: kbForm.description
          }
        })
        
        success.value = 'Knowledge base updated successfully!'
        setTimeout(() => success.value = '', 3000)
        
        await fetchKnowledgeBases()
        editingKB.value = null
        
        // Refresh the selected KB
        const updatedKB = knowledgeBases.value.find(kb => kb.id === selectedKB.value?.id)
        if (updatedKB) {
          selectKB(updatedKB)
        }
      } catch (err) {
        console.error('Error updating knowledge base:', err)
        error.value = 'Failed to update knowledge base'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const closeUploadForm = () => {
      showUploadForm.value = false
      selectedFile.value = null
      uploadForm.title = ''
      uploadForm.tags = ''
      uploadForm.category = ''
      uploadForm.description = ''
    }

    const handleFileSelect = (event) => {
      const files = event.target.files
      if (files.length > 0) {
        selectedFile.value = files[0]
        if (!uploadForm.title) {
          uploadForm.title = files[0].name.replace(/\.[^/.]+$/, '')
        }
      }
    }

    const handleFileDrop = (event) => {
      isDragOver.value = false
      const files = event.dataTransfer.files
      if (files.length > 0) {
        selectedFile.value = files[0]
        if (!uploadForm.title) {
          uploadForm.title = files[0].name.replace(/\.[^/.]+$/, '')
        }
      }
    }

    const removeFile = () => {
      selectedFile.value = null
      uploadForm.title = ''
    }

    const uploadDocument = async () => {
      if (!selectedFile.value || !uploadForm.knowledge_base_id) return
      
      isUploading.value = true
      
      try {
        const formData = new FormData()
        formData.append('file', selectedFile.value)
        formData.append('title', uploadForm.title || selectedFile.value.name)
        formData.append('description', uploadForm.description)
        formData.append('category', uploadForm.category)
        
        const tags = uploadForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        formData.append('tags', JSON.stringify(tags))
        
        // Include kb_name for compatibility with backend expectations
        const kb = knowledgeBases.value.find(k => k.id === uploadForm.knowledge_base_id)
        if (kb?.name) {
          formData.append('kb_name', kb.name)
        }
        
        await store.dispatch('knowledgeBases/uploadDocument', {
          kbId: uploadForm.knowledge_base_id,
          formData
        })
        
        success.value = 'Document uploaded successfully!'
        setTimeout(() => success.value = '', 3000)
        
        closeUploadForm()
        
        // Refresh documents if we have a selected KB
        if (selectedKB.value) {
          await fetchDocuments(selectedKB.value.id)
        }
      } catch (err) {
        console.error('Error uploading document:', err)
        error.value = 'Failed to upload document'
        setTimeout(() => error.value = '', 3000)
      } finally {
        isUploading.value = false
      }
    }

    const filterDocuments = () => {
      let filtered = [...documents.value]
      
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(doc => 
          (doc.title || doc.name).toLowerCase().includes(query) ||
          (doc.description || '').toLowerCase().includes(query) ||
          (doc.tags || []).some(tag => tag.toLowerCase().includes(query))
        )
      }
      
      if (selectedCategory.value) {
        filtered = filtered.filter(doc => doc.category === selectedCategory.value)
      }
      
      filteredDocuments.value = filtered
      sortDocuments()
    }

    const sortDocuments = () => {
      filteredDocuments.value.sort((a, b) => {
        switch (sortBy.value) {
          case 'name':
            return (a.title || a.name).localeCompare(b.title || b.name)
          case 'size':
            return (b.metadata?.size || 0) - (a.metadata?.size || 0)
          case 'category':
            return (a.category || '').localeCompare(b.category || '')
          case 'created_at':
          default:
            return new Date(b.created_at) - new Date(a.created_at)
        }
      })
    }

    const viewDocument = (doc) => {
      // Implement document viewer
      console.log('View document:', doc)
    }

    const downloadDocument = (doc) => {
      // Implement document download
      console.log('Download document:', doc)
    }

    const deleteDocument = async (doc) => {
      if (!confirm(`Are you sure you want to delete "${doc.title || doc.name}"?`)) return
      
      try {
        await store.dispatch('knowledgeBases/deleteDocument', {
          knowledgeBaseId: selectedKB.value.id,
          documentId: doc.id
        })
        success.value = 'Document deleted successfully!'
        setTimeout(() => success.value = '', 3000)
        
        if (selectedKB.value) {
          await fetchDocuments(selectedKB.value.id)
        }
      } catch (err) {
        console.error('Error deleting document:', err)
        error.value = 'Failed to delete document'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const deleteKB = async (kb) => {
      if (!kb) return
      if (!confirm(`Are you sure you want to archive \"${kb.name}\"?`)) return

      try {
        await store.dispatch('knowledgeBases/archive', kb.id)
        success.value = 'Knowledge base archived successfully!'
        setTimeout(() => success.value = '', 3000)

        // Refresh list to reflect archived state
        await fetchKnowledgeBases()

        if (selectedKB.value?.id === kb.id) {
          selectedKB.value = null
          showUploadForm.value = false
          editingKB.value = null
          filteredDocuments.value = []
        }
      } catch (err) {
        console.error('Error archiving knowledge base:', err)
        error.value = 'Failed to archive knowledge base'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const formatFileSize = (bytes) => {
      if (!bytes) return 'Unknown size'
      
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0
      
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }
      
      return `${size.toFixed(1)} ${units[unitIndex]}`
    }

    const formatTotalSize = () => {
      const totalBytes = documents.value.reduce((sum, doc) => sum + (doc.metadata?.size || 0), 0)
      return formatFileSize(totalBytes)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString()
    }

    const getFileIcon = (contentType) => {
      if (contentType?.includes('pdf')) return 'icon-file-pdf'
      if (contentType?.includes('word') || contentType?.includes('document')) return 'icon-file-word'
      if (contentType?.includes('text')) return 'icon-file-text'
      return 'icon-file'
    }

    const getFileType = (contentType) => {
      if (contentType?.includes('pdf')) return 'PDF'
      if (contentType?.includes('word') || contentType?.includes('document')) return 'Word'
      if (contentType?.includes('text')) return 'Text'
      return 'Document'
    }

    onMounted(async () => {
      await fetchKnowledgeBases()
    })

    return {
      knowledgeBases,
      documents,
      selectedKB,
      editingKB,
      selectedFile,
      error,
      success,
      showCreateKBForm,
      showUploadForm,
      isDragOver,
      isUploading,
      searchQuery,
      selectedCategory,
      sortBy,
      filteredDocuments,
      kbForm,
      uploadForm,
      selectKB,
      editKB,
      closeKBForm,
      createKB,
      updateKB,
      closeUploadForm,
      handleFileSelect,
      handleFileDrop,
      removeFile,
      uploadDocument,
      filterDocuments,
      sortDocuments,
      viewDocument,
      downloadDocument,
      deleteDocument,
      deleteKB,
      formatFileSize,
      formatTotalSize,
      formatDate,
      getFileIcon,
      getFileType
    }
  }
}
</script>

<style scoped>
/* Main Container */
.knowledge-base-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #ffffff;
  color: #2c3e50;
  border-bottom: 1px solid #e9ecef;
}

.page-header h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

/* Messages */
.error-message {
  background: linear-gradient(45deg, #ff6b6b, #ee5a52);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 2rem;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
}

.success-message {
  background: linear-gradient(45deg, #51cf66, #40c057);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 2rem;
  box-shadow: 0 2px 8px rgba(81, 207, 102, 0.3);
}

/* Main Content Layout */
.main-content {
  display: flex;
  flex: 1;
  gap: 1rem;
  padding: 1rem 1.25rem;
  overflow: hidden;
}

/* Left Panel - Knowledge Bases List */
.left-panel {
  width: 300px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
}

.panel-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.kb-count {
  background: #e8f5e8;
  color: #28a745;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem 1.5rem;
  color: #6c757d;
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h4 {
  margin: 1rem 0 0.5rem;
  color: #495057;
}

/* Knowledge Bases List */
.kb-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.kb-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.kb-item:hover {
  background: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.kb-item.active {
  background: linear-gradient(45deg, #e8f5e8, #f0fff4);
  border-color: #28a745;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.2);
}

.kb-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28a745, #20c997);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
  flex-shrink: 0;
}

.kb-info {
  flex: 1;
  min-width: 0;
}

.kb-info h4 {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kb-info p {
  margin: 0;
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.kb-meta {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.doc-count {
  background: #e8f5e8;
  color: #28a745;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.kb-status {
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #ffebee;
  color: #c62828;
}

.kb-status.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.kb-actions {
  opacity: 0;
  transition: opacity 0.2s;
}

.kb-item:hover .kb-actions {
  opacity: 1;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  background: #f8f9fa;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #e9ecef;
  color: #495057;
}

.btn-icon.danger:hover {
  background: #ffebee;
  color: #c62828;
}

/* Right Panel */
.right-panel {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Knowledge Base Form Panel */
.kb-form-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.kb-form {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

/* Upload Form Panel */
.upload-form-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.upload-form {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.file-upload-zone {
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  transition: all 0.2s;
  cursor: pointer;
}

.file-upload-zone.drag-over {
  border-color: #28a745;
  background: #f0fff4;
}

.upload-placeholder i {
  font-size: 3rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.upload-placeholder h4 {
  margin: 0 0 0.5rem;
  color: #495057;
}

.upload-placeholder p {
  margin: 0 0 1.5rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.file-preview {
  padding: 1rem;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
}

.file-info i {
  font-size: 2rem;
  color: #28a745;
}

.file-details {
  flex: 1;
}

.file-details h4 {
  margin: 0;
  color: #2c3e50;
}

.file-details p {
  margin: 0.25rem 0 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.btn-remove {
  width: 32px;
  height: 32px;
  border: none;
  background: #ffebee;
  color: #c62828;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-remove:hover {
  background: #f8d7da;
}

/* Documents Panel */
.documents-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.kb-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.doc-count-badge {
  background: #e8f5e8;
  color: #28a745;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.documents-toolbar {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 300px;
}

.search-box i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-box input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2.5rem;
  border: 1px solid #e9ecef;
  border-radius: 20px;
  font-size: 0.9rem;
}

.filter-controls {
  display: flex;
  gap: 0.5rem;
}

.filter-controls select {
  padding: 0.5rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
}

.kb-overview {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.kb-description {
  margin: 0 0 1rem;
  color: #6c757d;
  line-height: 1.5;
}

.kb-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-item .label {
  font-weight: 600;
  color: #495057;
}

.stat-item .value {
  color: #6c757d;
}

.documents-section {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.no-documents {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6c757d;
  text-align: center;
}

.no-documents i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.document-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
}

.document-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-color: #28a745;
}

.doc-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.doc-icon i {
  font-size: 1.5rem;
  color: #28a745;
}

.doc-actions {
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.document-card:hover .doc-actions {
  opacity: 1;
}

.doc-content {
  padding: 1rem;
}

.doc-content h4 {
  margin: 0 0 0.5rem;
  color: #2c3e50;
  font-size: 1rem;
  line-height: 1.3;
}

.doc-description {
  margin: 0 0 1rem;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.doc-meta {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
}

.meta-label {
  color: #6c757d;
  font-weight: 500;
}

.meta-value {
  color: #495057;
}

.meta-value.category {
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
}

.meta-value.category.documentation { background: #e3f2fd; color: #1976d2; }
.meta-value.category.research { background: #f3e5f5; color: #7b1fa2; }
.meta-value.category.policy { background: #fff3e0; color: #f57c00; }
.meta-value.category.tutorial { background: #e8f5e8; color: #388e3c; }
.meta-value.category.reference { background: #fce4ec; color: #c2185b; }
.meta-value.category.other { background: #f5f5f5; color: #616161; }
.meta-value.category.uncategorized { background: #f5f5f5; color: #9e9e9e; }

.doc-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  background: #e9ecef;
  color: #495057;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Welcome Panel */
.welcome-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.welcome-content {
  text-align: center;
  color: #6c757d;
  max-width: 500px;
}

.welcome-content i {
  font-size: 5rem;
  margin-bottom: 1.5rem;
  opacity: 0.3;
  color: #28a745;
}

.welcome-content h3 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.5rem;
}

.welcome-content p {
  margin: 0 0 2rem;
  line-height: 1.6;
}

.welcome-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

/* Form Elements */
.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

input, textarea, select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

input:focus, textarea:focus, select:focus {
  outline: none;
  border-color: #28a745;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.form-actions {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  border-radius: 4px;
  transition: color 0.2s;
}

.btn-close:hover {
  color: #495057;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #e9ecef;
}

.btn-secondary:hover {
  background: #e9ecef;
  color: #495057;
}

.btn-outline {
  background: transparent;
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
}

.btn-outline:hover {
  background: rgba(255,255,255,0.1);
}

/* Icons */
.icon-upload:before { content: '📤'; }
.icon-plus:before { content: '+'; }
.icon-knowledge:before { content: '📚'; }
.icon-folder:before { content: '📁'; }
.icon-edit:before { content: '✏️'; }
.icon-close:before { content: '✕'; }
.icon-upload-big:before { content: '📤'; }
.icon-file:before { content: '📄'; }
.icon-file-pdf:before { content: '📕'; }
.icon-file-word:before { content: '📘'; }
.icon-file-text:before { content: '📄'; }
.icon-search:before { content: '🔍'; }
.icon-document:before { content: '📄'; }
.icon-download:before { content: '⬇️'; }
.icon-trash:before { content: '🗑️'; }
.icon-knowledge-large:before { content: '📚'; }

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
  
  .left-panel {
    width: 100%;
    max-height: 300px;
  }
  
  .documents-grid {
    grid-template-columns: 1fr;
  }
  
  .documents-toolbar {
    flex-direction: column;
  }
  
  .welcome-actions {
    flex-direction: column;
  }
}
</style>

